import React, { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, Loader2, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import CurrencyInput from '../ui/CurrencyInput';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { Sample } from '../../types';
import { supabase } from '../../lib/supabase';
import { useMemo } from 'react';
import EntityModal from './EntityModal';
import ErrorModal from './ErrorModal';
import ConfirmModal from './ConfirmModal';
import { formatCurrency } from '../../utils/formatters';
import { getCurrentArgentinaISOString } from '../../utils/dateUtils';
import { logChangesAndNotify } from '../../utils/changeLog';
import ChangeHistoryModal from './ChangeHistoryModal';
import { useAuth } from '../../context/AuthContext';
import PaymentReceiptField from '../shared/PaymentReceiptField';
import { isPaymentReceiptValid, notifyPaymentReceiptUploaded } from '../../utils/paymentReceipt';

interface SampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (sample: Omit<Sample, 'id' | 'created_at' | 'updated_at'>) => void;
  initialData?: Sample;
}

// Define localStorage keys
const STORAGE_KEYS = {
  APPROVED_FILE: 'sample_approved_file',
  DTF_FILE: 'sample_dtf_file',
  EMBROIDERY_FILE: 'sample_embroidery_file',
  PAYMENT_RECEIPT: 'sample_payment_receipt',
  SHIPPING_LABEL: 'sample_shipping_label',
  INVOICE_FILE: 'sample_invoice_file',
  FORM_DATA: 'sample_form_data',
  FILE_NAMES: 'sample_file_names'
};

const SampleModal: React.FC<SampleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const { clients, addClient, salespersons } = useAppStore();
  const { userRole } = useAuth();
  const hideFinancialFields = userRole === 'Producción';
  const canBypassRequiredFields = userRole === 'Producción' || userRole === 'Administrador' || userRole === 'Super Admin';
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    approved_file: '',
    dtf_file: '',
    embroidery_file: '',
    start_date: getCurrentArgentinaISOString(),
    delivery_date: '',
    salesperson: '' as const,
    payment_receipt: '',
    cash_payment: false,
    shipping_label: '',
    invoice_file: '',
    status: 'pending' as const,
    sample_value: '0',
    comment: '',
  });

  const [isUploading, setIsUploading] = useState({
    approved: false,
    dtf: false,
    embroidery: false,
    receipt: false,
    shipping: false,
    invoice: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fileToDelete, setFileToDelete] = useState<{field: string, fileName: string} | null>(null);
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});
  const [formModified, setFormModified] = useState(false);
  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Historial de cambios
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [createdByEmail, setCreatedByEmail] = useState<string | null>(null);
  
  // Prepare salesperson options
  const salespersonOptions = useMemo(() => [
    { value: '', label: 'Seleccionar vendedor...' },
    ...salespersons.map(sp => ({ value: sp.name, label: `${sp.name} (${sp.email})` }))
  ], [salespersons]);

  // Load data from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      try {
        /* ─── NUEVO ─── */
        if (!initialData) {
          // Es "Nueva Muestra" → limpiar todo
          localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
          localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
          localStorage.removeItem(STORAGE_KEYS.EMBROIDERY_FILE);
          localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
          localStorage.removeItem(STORAGE_KEYS.SHIPPING_LABEL);
          localStorage.removeItem(STORAGE_KEYS.INVOICE_FILE);
          localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
          localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);

          // Reiniciar campos del formulario
          setFormData({
            name: '',
            client_id: '',
            approved_file: '',
            dtf_file: '',
            embroidery_file: '',
            start_date: getCurrentArgentinaISOString(),
            delivery_date: '',
            salesperson: '',
            payment_receipt: '',
            cash_payment: false,
            shipping_label: '',
            invoice_file: '',
            status: 'pending',
            sample_value: '0',
            comment: '',
          });
          setFileNames({});
          setFormModified(false);
        } else {
          // Es "Editar Muestra" → cargar datos existentes
          setFormData({
            name: initialData.name || '',
            client_id: initialData.client_id || '',
            approved_file: initialData.approved_file || '',
            dtf_file: initialData.dtf_file || '',
            embroidery_file: initialData.embroidery_file || '',
            start_date: (initialData as any).start_date || initialData.created_at?.slice(0, 10) || getCurrentArgentinaISOString(),
            delivery_date: initialData.delivery_date || '',
            salesperson: initialData.salesperson || '',
            payment_receipt: initialData.payment_receipt || '',
            cash_payment: Boolean((initialData as any).cash_payment),
            shipping_label: initialData.shipping_label || '',
            invoice_file: (initialData as any).invoice_file || '',
            status: initialData.status || 'pending',
            sample_value: initialData.sample_value?.toString() || '0',
            comment: initialData.comment || '',
          });
          setFormModified(false);
        }

        // Cargar archivos desde localStorage
        const savedApprovedFile = localStorage.getItem(STORAGE_KEYS.APPROVED_FILE);
        const savedDtfFile = localStorage.getItem(STORAGE_KEYS.DTF_FILE);
        const savedEmbroideryFile = localStorage.getItem(STORAGE_KEYS.EMBROIDERY_FILE);
        const savedPaymentReceipt = localStorage.getItem(STORAGE_KEYS.PAYMENT_RECEIPT);
        const savedShippingLabel = localStorage.getItem(STORAGE_KEYS.SHIPPING_LABEL);
        const savedFileNames = localStorage.getItem(STORAGE_KEYS.FILE_NAMES);

        if (savedApprovedFile && !initialData) {
          setFormData(prev => ({ ...prev, approved_file: savedApprovedFile }));
        }
        if (savedDtfFile && !initialData) {
          setFormData(prev => ({ ...prev, dtf_file: savedDtfFile }));
        }
        if (savedEmbroideryFile && !initialData) {
          setFormData(prev => ({ ...prev, embroidery_file: savedEmbroideryFile }));
        }
        if (savedPaymentReceipt && !initialData) {
          setFormData(prev => ({ ...prev, payment_receipt: savedPaymentReceipt }));
        }
        if (savedShippingLabel && !initialData) {
          setFormData(prev => ({ ...prev, shipping_label: savedShippingLabel }));
        }
        const savedInvoiceFile = localStorage.getItem(STORAGE_KEYS.INVOICE_FILE);
        if (savedInvoiceFile && !initialData) {
          setFormData(prev => ({ ...prev, invoice_file: savedInvoiceFile }));
        }
        if (savedFileNames) {
          setFileNames(JSON.parse(savedFileNames));
        }
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
      }
    }
  }, [isOpen, initialData]);

  // Obtener creador
  useEffect(() => {
    const fetchCreator = async () => {
      if (!initialData) return;
      const { data } = await supabase
        .from('entity_change_log')
        .select('changed_by_email, created_at')
        .eq('entity_type', 'sample')
        .eq('entity_id', initialData.id)
        .order('created_at', { ascending: true })
        .limit(1);
      const emailFromLog = data?.[0]?.changed_by_email || null;
      if (emailFromLog) {
        setCreatedByEmail(emailFromLog);
      } else {
        const sp = salespersons.find(sp => sp.name === initialData.salesperson);
        setCreatedByEmail(sp?.email || null);
      }
    };
    fetchCreator();
  }, [initialData]);

  useEffect(() => {
    if (!isHistoryOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsHistoryOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isHistoryOpen]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (isOpen && !initialData) {
      try {
        localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formData));
        localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify(fileNames));
        if (formData.invoice_file) {
          localStorage.setItem(STORAGE_KEYS.INVOICE_FILE, formData.invoice_file);
        }
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    }
  }, [formData, fileNames, isOpen, initialData]);

  const paymentReceiptCtx = () => ({
    hideFinancialFields,
    canBypassRequiredFields,
    userRole,
    cashPayment: Boolean(formData.cash_payment),
    paymentReceipt: formData.payment_receipt || '',
  });

  const handleFileUpload = async (files: FileList, type: 'approved' | 'dtf' | 'embroidery' | 'receipt' | 'shipping' | 'invoice') => {
    if (!files || files.length === 0) return;

    // Validar tamaño del archivo (límite de 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB en bytes
    const file = files[0];
    if (file.size > maxSize) {
      setErrorMessage(`El archivo "${file.name}" es demasiado grande. El tamaño máximo permitido es 10MB.`);
      setErrorModalOpen(true);
      return;
    }

    setIsUploading(prev => ({ ...prev, [type]: true }));

    try {
      // Sanitizar nombre de archivo para Supabase (sin acentos/espacios/caracteres especiales)
      const sanitizeFileName = (name: string) => {
        // Separar diacríticos y removerlos
        const withoutDiacritics = name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        // Reemplazar espacios por guiones y quitar caracteres no permitidos (mantener letras, números, ., -, _)
        const safe = withoutDiacritics
          .replace(/ñ/gi, 'n')
          .replace(/[^a-zA-Z0-9._-]+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$|\.-/g, '-');
        return safe;
      };

      const originalName = file.name;
      const extensionMatch = originalName.match(/\.[^.]+$/);
      const baseSafe = sanitizeFileName(originalName.replace(/\.[^.]+$/, ''));
      const ext = extensionMatch ? extensionMatch[0].toLowerCase() : '';
      const uniquePrefix = uuidv4();
      const safeFileName = `${uniquePrefix}-${baseSafe}${ext}`;
      const folder = type === 'invoice' ? 'invoice' : 'samples';
      const filePath = `${folder}/${safeFileName}`;

      const { error: uploadError } = await supabase.storage
        .from('samples-files')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('samples-files')
        .getPublicUrl(filePath);

      const fileUrl = data.publicUrl;

      // Map logical type to form field name
      const uploadStateMap: Record<typeof type, keyof typeof formData> = {
        approved: 'approved_file',
        dtf: 'dtf_file',
        embroidery: 'embroidery_file',
        receipt: 'payment_receipt',
        shipping: 'shipping_label',
        invoice: 'invoice_file',
      } as const;

      const targetField = uploadStateMap[type];

      // Update form data
      setFormData(prev => ({ ...prev, [targetField]: fileUrl }));
      setFormModified(true);

      // Update file names
      setFileNames(prev => ({
        ...prev,
        [targetField as string]: [originalName]
      }));

      // Save to localStorage
      if (!initialData) {
        const storageKeyMap: Record<typeof type, keyof typeof STORAGE_KEYS> = {
          approved: 'APPROVED_FILE',
          dtf: 'DTF_FILE',
          embroidery: 'EMBROIDERY_FILE',
          receipt: 'PAYMENT_RECEIPT',
          shipping: 'SHIPPING_LABEL',
          invoice: 'INVOICE_FILE',
        } as const;
        localStorage.setItem(STORAGE_KEYS[storageKeyMap[type]], fileUrl);
      }

      if (type === 'receipt') {
        const clientName = clients.find((c) => c.id === formData.client_id)?.name || '—';
        await notifyPaymentReceiptUploaded({
          entityType: 'sample',
          entityId: initialData?.id || null,
          entityName: formData.name || 'Muestra',
          clientName,
          receiptUrl: fileUrl,
          salesperson: formData.salesperson,
        });
      }

    } catch (error: any) {
      console.error('Error uploading file:', error);
      if (error.statusCode === '413' || error.message?.includes('Payload too large')) {
        setErrorMessage(`El archivo "${file.name}" es demasiado grande. El tamaño máximo permitido es 10MB.`);
      } else {
        setErrorMessage(`Error al subir el archivo "${file.name}". Por favor, inténtalo de nuevo.`);
      }
      setErrorModalOpen(true);
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDeleteFile = (field: string) => {
    const fileName = fileNames[field]?.[0] || 'este archivo';
    setFileToDelete({ field, fileName });
    setIsDeleteFileModalOpen(true);
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      const { field } = fileToDelete;
      const currentFileUrl = formData[field as keyof typeof formData] as string;

      if (currentFileUrl) {
        // Extract file path from URL
        const urlParts = currentFileUrl.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const filePath = `samples/${fileName}`;

        // Delete from storage
        const { error } = await supabase.storage
          .from('samples-files')
          .remove([filePath]);

        if (error) {
          console.error('Error deleting file from storage:', error);
        }
      }

      // Update form data
      setFormData(prev => ({ ...prev, [field]: '' }));
      setFormModified(true);

      // Update file names
      setFileNames(prev => {
        const newFileNames = { ...prev };
        delete newFileNames[field];
        return newFileNames;
      });

      // Remove from localStorage
      if (!initialData) {
        localStorage.removeItem(STORAGE_KEYS[`${field.toUpperCase()}` as keyof typeof STORAGE_KEYS]);
      }

    } catch (error) {
      console.error('Error deleting file:', error);
      setErrorMessage('Error al eliminar el archivo. Por favor, inténtalo de nuevo.');
      setErrorModalOpen(true);
    } finally {
      setIsDeleteFileModalOpen(false);
      setFileToDelete(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canBypassRequiredFields && !hideFinancialFields && !formData.invoice_file) {
      setErrorMessage('El archivo de factura es obligatorio');
      setErrorModalOpen(true);
      return;
    }
    if (!canBypassRequiredFields && !isPaymentReceiptValid(paymentReceiptCtx())) {
      setErrorMessage('El comprobante de pago es obligatorio');
      setErrorModalOpen(true);
      return;
    }
    
    try {
      setIsSaving(true);
      // Si es edición, registrar cambios antes de guardar
      if (initialData) {
        await logChangesAndNotify({
          entityType: 'sample',
          entityId: initialData.id,
          entityName: initialData.name,
          previous: initialData as any,
          next: { ...initialData, ...formData } as any,
          includeFields: ['name','client_id','delivery_date','status','salesperson','comment','approved_file','dtf_file','embroidery_file','payment_receipt','shipping_label','invoice_file']
        });
      }

      await onSave(formData);

      // Clear localStorage after successful save
      if (!initialData) {
        localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
        localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
        localStorage.removeItem(STORAGE_KEYS.EMBROIDERY_FILE);
        localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
        localStorage.removeItem(STORAGE_KEYS.SHIPPING_LABEL);
        localStorage.removeItem(STORAGE_KEYS.INVOICE_FILE);
        localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
        localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
      }
      
      setFormModified(false);
    } catch (error) {
      console.error('Error saving sample:', error);
      setErrorMessage('Error al guardar la muestra. Por favor, inténtalo de nuevo.');
      setErrorModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseClick = () => {
    if (formModified) {
      setIsConfirmCloseModalOpen(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    // Clear localStorage when closing without saving
    if (!initialData) {
      localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
      localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
      localStorage.removeItem(STORAGE_KEYS.EMBROIDERY_FILE);
      localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
      localStorage.removeItem(STORAGE_KEYS.SHIPPING_LABEL);
      localStorage.removeItem(STORAGE_KEYS.INVOICE_FILE);
      localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
      localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
    }
    setFormModified(false);
    setIsConfirmCloseModalOpen(false);
    onClose();
  };

  const getFileNameFromUrl = (url: string): string => {
    if (!url) return '';
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1];
  };

  const renderFileLinks = (fileUrl: string, field: string) => {
    if (!fileUrl) return null;

    const fileName = fileNames[field]?.[0] || getFileNameFromUrl(fileUrl);
    
    return (
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline text-sm"
      >
        {fileName}
      </a>
    );
  };

  const formatSampleValueAsCurrency = (value: string): string => {
    const numericValue = parseFloat(value) || 0;
    return formatCurrency(numericValue);
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleCloseClick}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 max-h-[90vh] rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none flex flex-col min-h-0 z-50">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {initialData ? 'Editar' : 'Nueva'} Muestra
                  </h2>
                  {initialData && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      Creado: {new Date(initialData.created_at).toLocaleString()} · {createdByEmail || 'usuario desconocido'}
                    </p>
                  )}
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600" aria-label="Cerrar" onClick={(e) => { e.preventDefault(); handleCloseClick(); }}>
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Nombre *</label>
                    <input
                      type="text"
                      placeholder="Ej: Muestra remera básica"
                      value={formData.name}
                      onChange={(e) => { setFormData(prev => ({ ...prev, name: e.target.value })); setFormModified(true); }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      required={!canBypassRequiredFields}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Cliente *</label>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 min-w-0 [&>div]:!mb-0">
                        <Select label="" value={formData.client_id} onChange={(value) => { setFormData(prev => ({ ...prev, client_id: value })); setFormModified(true); }}
                          options={clients.map(client => ({ value: client.id, label: client.name }))} required={!canBypassRequiredFields}
                        />
                      </div>
                      <Button type="button" variant="outline" onClick={() => setIsClientModalOpen(true)} icon={<Plus className="h-4 w-4" />} className="h-12 shrink-0 self-end">Nuevo</Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Fecha de inicio *</label>
                    <input type="date" value={formData.start_date} onChange={(e) => { setFormData(prev => ({ ...prev, start_date: e.target.value })); setFormModified(true); }} className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" required={!canBypassRequiredFields} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Fecha de entrega *</label>
                    <input type="date" value={formData.delivery_date} onChange={(e) => { setFormData(prev => ({ ...prev, delivery_date: e.target.value })); setFormModified(true); }} className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" required={!canBypassRequiredFields} />
                  </div>
                  <div className="sm:col-span-2 [&>div]:!mb-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Vendedor *</label>
                    <Select label="" value={formData.salesperson} onChange={(value) => { setFormData(prev => ({ ...prev, salesperson: value })); setFormModified(true); }}
                      options={salespersonOptions} required={!canBypassRequiredFields}
                    />
                  </div>
                </div>

                {!hideFinancialFields && (
                <CurrencyInput label="Valor de la muestra" value={formData.sample_value} onChange={(value) => { setFormData(prev => ({ ...prev, sample_value: value })); setFormModified(true); }}
                  id="sample_value" name="sample_value" required className="!mb-0"
                />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Ficha Aprobada *</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) { const dt = new DataTransfer(); dt.items.add(f[0]); handleFileUpload(dt.files, 'approved'); } }} className="hidden" id="approved-file" />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('approved-file')?.click()} disabled={isUploading.approved || !!formData.approved_file}
                        icon={isUploading.approved ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                        {formData.approved_file ? 'Cambiar' : 'Subir'}
                      </Button>
                      {formData.approved_file && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.approved_file, 'approved_file')}</div><button type="button" onClick={() => handleDeleteFile('approved_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button></>)}
                    </div>
                    {!formData.approved_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                  </div>
                  {!hideFinancialFields && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Factura *</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) { const dt = new DataTransfer(); dt.items.add(f[0]); handleFileUpload(dt.files, 'invoice'); } }} className="hidden" id="invoice-file" />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('invoice-file')?.click()} disabled={isUploading.invoice || !!formData.invoice_file}
                        icon={isUploading.invoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                        {formData.invoice_file ? 'Cambiar' : 'Subir'}
                      </Button>
                      {formData.invoice_file && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.invoice_file, 'invoice_file')}</div><button type="button" onClick={() => handleDeleteFile('invoice_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button></>)}
                    </div>
                    {!formData.invoice_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                  </div>
                  )}
                </div>

                <div className="pt-2">
                  <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
                    {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    Más opciones
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-100 dark:border-gray-800">
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Archivo DTF</span>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'dtf'); }} className="hidden" id="dtf-file" multiple />
                          <Button type="button" variant="outline" onClick={() => document.getElementById('dtf-file')?.click()} disabled={isUploading.dtf}
                            icon={isUploading.dtf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm">Subir DTF</Button>
                          {formData.dtf_file && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.dtf_file, 'dtf_file')}</div><button type="button" onClick={() => handleDeleteFile('dtf_file')} className="text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></>)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Archivo Bordado</span>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'embroidery'); }} className="hidden" id="embroidery-file" multiple />
                          <Button type="button" variant="outline" onClick={() => document.getElementById('embroidery-file')?.click()} disabled={isUploading.embroidery}
                            icon={isUploading.embroidery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm">Subir Bordado</Button>
                          {formData.embroidery_file && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.embroidery_file, 'embroidery_file')}</div><button type="button" onClick={() => handleDeleteFile('embroidery_file')} className="text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></>)}
                        </div>
                      </div>
                      {!hideFinancialFields && (
                        <PaymentReceiptField
                          inputId="sample-payment-receipt"
                          paymentReceipt={formData.payment_receipt}
                          cashPayment={Boolean(formData.cash_payment)}
                          userRole={userRole}
                          hideFinancialFields={hideFinancialFields}
                          canBypassRequiredFields={canBypassRequiredFields}
                          isUploading={isUploading.receipt}
                          onUpload={(files) => handleFileUpload(files, 'receipt')}
                          onDelete={() => handleDeleteFile('payment_receipt')}
                          onCashPaymentChange={(checked) => { setFormData((prev) => ({ ...prev, cash_payment: checked })); setFormModified(true); }}
                          renderFileLinks={renderFileLinks}
                        />
                      )}
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Etiqueta de envío</span>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'shipping'); }} className="hidden" id="shipping-label" multiple />
                          <Button type="button" variant="outline" onClick={() => document.getElementById('shipping-label')?.click()} disabled={isUploading.shipping}
                            icon={isUploading.shipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm">Subir Etiqueta</Button>
                          {formData.shipping_label && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.shipping_label, 'shipping_label')}</div><button type="button" onClick={() => handleDeleteFile('shipping_label')} className="text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></>)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Comentarios</span>
                        <textarea id="comment" name="comment" rows={2} value={formData.comment}
                          onChange={(e) => { setFormData(prev => ({ ...prev, comment: e.target.value })); setFormModified(true); }}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400" placeholder="Opcional..."
                        />
                      </div>
                      {initialData && (
                        <Select label="Estado" value={formData.status} onChange={(value) => { setFormData(prev => ({ ...prev, status: value as Sample['status'] })); setFormModified(true); }}
                          options={[{ value: 'pending', label: 'Pendiente' }, { value: 'in_progress', label: 'En Proceso' }, { value: 'finished', label: 'Terminado' }, { value: 'shipped', label: 'Despachado' }]} required={!canBypassRequiredFields}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2 shrink-0">
                {initialData ? (
                  <button
                    type="button"
                    className="text-xs underline text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={async () => {
                      setIsHistoryOpen(true);
                      setIsLoadingHistory(true);
                      const { data } = await supabase
                        .from('entity_change_log')
                        .select('*')
                        .eq('entity_type', 'sample')
                        .eq('entity_id', initialData.id)
                        .order('created_at', { ascending: false });
                      setHistoryItems(data || []);
                      setIsLoadingHistory(false);
                    }}
                  >
                    Historial de cambios
                  </button>
                ) : <span />}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseClick}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving || (!canBypassRequiredFields && (!formData.name || !formData.client_id || !formData.delivery_date || !formData.approved_file || (!hideFinancialFields && (!formData.invoice_file || !formData.sample_value || parseFloat(formData.sample_value) <= 0 || !isPaymentReceiptValid(paymentReceiptCtx()))) || !formData.salesperson))}
                >
                  {isSaving ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Guardando...
                    </span>
                  ) : (
                    initialData ? 'Guardar Cambios' : 'Crear Muestra'
                  )}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ChangeHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        entityType="sample"
        entityId={initialData?.id}
        createdAt={initialData?.created_at}
        createdByEmail={createdByEmail || undefined}
      />

      <EntityModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onSave={(client) => {
          addClient(client);
          setFormData(prev => ({ ...prev, client_id: client.id }));
          setFormModified(true);
        }}
      />

      <ErrorModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        message={errorMessage}
      />

      <ConfirmModal
        isOpen={isDeleteFileModalOpen}
        onClose={() => setIsDeleteFileModalOpen(false)}
        onConfirm={confirmDeleteFile}
        title="Eliminar archivo"
        message={`¿Estás seguro de que quieres eliminar ${fileToDelete?.fileName}?`}
      />

      <ConfirmModal
        isOpen={isConfirmCloseModalOpen}
        onClose={() => setIsConfirmCloseModalOpen(false)}
        onConfirm={confirmClose}
        title="¿Seguro que querés cancelar?"
        message="Se borrarán los datos cargados hasta el momento."
        confirmText="Aceptar"
        variant="warning"
      />
    </>
  );
};

export default SampleModal;